import axios from "axios";

import { apiConfig } from "../config/authConfig";
import { CredentialManager } from "../classes/credentialManager";

import { ITrailer, ITrailerLength } from "../types/trailer";
import { IVendorLoadMinimums } from "../types/vendor";

export namespace TrailerService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Convert one api response object to one Trailer.
	 * @param data Trailer response data from an API.
	 **********/
	export function mapTrailer(data: any) {
		let trailer: ITrailer = {
			trailerId: data.id,
			customerId: data.customerId,
			customerName: data.customerName,
			transporterId: data.transporterId,
			transporterName: data.transporterName,
			vendorId: data.vendorId,
			vendorName: data.vendorName,
			trailerTypeId: data.trailerTypeId,
			trailerTypeName: data.trailerTypeName,
			trailerNumber: data.trailerNumber,
			location: data.location,
			full: data.full
		}

		return trailer;
	}

	/**********
	 * Convert an array of api response objects to an array of Trailers.
	 * @param data Trailer data from an API response.
	 **********/
	export function mapTrailers(data: any) {
		let trailers: ITrailer[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				trailers.push(mapTrailer(item));
			})
		}

		return trailers;
	}

	/**********
	 * Gets all trailers.
	 **********/
	export async function getTrailers() {
		const apiResponse = axios.get(
			apiConfig.default + "/trailer/all",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Convert one api response object to one TrailerLength.
	 * @param data TrailerLength response data from an API.
	 **********/
	export function mapTrailerLength(data: any) {
		let trailerLength: ITrailerLength = {
			id: data.id,
			length: data.length,
			rows: data.rows,
			halfRow: data.halfRow,
			trailerMaxWeight: data.trailerMaxWeight
		}

		return trailerLength;
	}

	/**********
	 * Gets all trailers.
	 **********/
	export async function getTrailersByCustomers(employeeId: string | undefined) {
		const apiResponse = axios.get(
			apiConfig.default + "/trailer/byemployee/" + employeeId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`,
					'Content-Type': 'application/json',
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all by customer, vendor, transporter.
	 **********/
	export async function getAvailableTrailers(customerId: number, vendorId: number, transporterId: number) {
		const apiResponse = axios.get(
			apiConfig.default + "/trailer/bycvt/" + customerId + "/" + vendorId + "/" + transporterId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets a trailer.
	 * @param trailerId The id of the trailer.
	 **********/
	export async function getTrailer(trailerId: string) {
		const employeeResponse = axios.get(
			apiConfig.default + "/trailer/" + trailerId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return employeeResponse;
	}

	/**********
	 * Gets length information about trailers.
	 * @param trailerLengthId The id of the trailer length record.
	 **********/
	export async function getTrailerLength(trailerLengthId: string) {
		const employeeResponse = axios.get(
			apiConfig.default + "/trailer/length/" + trailerLengthId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return employeeResponse;
	}

	/**********
	 * Gets the minimum load weight allowed by the vendor for various trailer types.
	 * @param vendorId The id of the vendor.
	 **********/
	export async function getLoadMinimumsForVendor(vendorId: string) {
		const apiResponse = axios.get(
			apiConfig.default + `/trailer/vendor/${vendorId}/minimums`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Add a new trailer starting.
	 * @param trailer The trailer's details.
	 **********/
	export async function addTrailer(trailer: ITrailer) {
		const apiResponse = axios.put(
			apiConfig.default + "/trailer",
			trailer,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update the number/name of an existing trailer.
	 * @param trailerId The id of the trailer.
	 * @param number The new trailer number.
	 * @param loadId Optionally, the ID of the load this trailer is assigned to.
	 **********/
	export async function updateTrailerNumber(trailerId: number, number: string, loadId?: number) {
		const apiResponse = axios.post(
			apiConfig.default + `/trailer/${trailerId}/number`,
			{ number: number, loadId: loadId },
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update the minimum load weight allowed by the vendor for various trailer types.
	 * @param loadMinimums The load minimums details.
	 * @param vendorId The id of the vendor.
	 **********/
	export async function updateLoadMinimumsForVendor(loadMinimums: IVendorLoadMinimums, vendorId: string) {
		const apiResponse = axios.put(
			apiConfig.default + `/trailer/vendor/${vendorId}/minimums`,
			loadMinimums,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Remove (mark as not available) a trailer starting.
	 * @param trailerId The id of the trailer.
	 **********/
	export async function removeTrailer(trailerId: string) {
		const apiResponse = axios.post(
			apiConfig.default + "/trailer/" + trailerId,
			{},
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

}

